import { supabase } from "@/integrations/supabase/client";
import { CvTemplate, CreateCvTemplateData, UpdateCvTemplateData } from "@/types/cvTemplate";

function mapDbRow(row: any): CvTemplate {
	return {
		id: row.id,
		name: row.name,
		slug: row.slug,
		description: row.description,
		content: row.content,
		header_html: row.header_html,
		footer_html: row.footer_html,
		sections: row.sections || ["contact", "summary", "experience", "skills"],
		styles: row.styles,
		color_scheme: row.color_scheme || "classic",
		custom_colors: row.custom_colors || undefined,
		layout: row.layout || "standard",
		organization_uuid: row.organization_uuid,
		created_by: row.created_by,
		created_at: row.created_at,
		updated_at: row.updated_at,
		is_default: row.is_default || false,
	};
}

export class CvTemplateService {
	static async getAll(): Promise<CvTemplate[]> {
		const { data, error } = await supabase
			.from('cv_templates')
			.select('*')
			.order('created_at', { ascending: false });

		if (error)
			throw new Error(`Failed to fetch CV templates: ${error.message}`);

		return (data || []).map(mapDbRow);
	}

	static async getById(id: string): Promise<CvTemplate | null> {
		const { data, error } = await supabase
			.from('cv_templates')
			.select('*')
			.eq('id', id)
			.single();

		if (error) {
			if (error.code === 'PGRST116')
				return null;
			throw new Error(`Failed to fetch CV template: ${error.message}`);
		}

		return data ? mapDbRow(data) : null;
	}

	static async create(templateData: CreateCvTemplateData): Promise<CvTemplate> {
		const { data: { user } } = await supabase.auth.getUser();
		if (!user)
			throw new Error('User not authenticated');

		const { data: userOrg } = await supabase
			.from('user_organization')
			.select('organization_uuid')
			.eq('user_uuid', user.id)
			.single();

		if (!userOrg)
			throw new Error('User organization not found');

		const slug = templateData.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

		const { data, error } = await supabase
			.from('cv_templates')
			.insert({
				name: templateData.name,
				slug,
				description: templateData.description,
				content: templateData.content || "",
				header_html: templateData.header_html,
				footer_html: templateData.footer_html,
				sections: templateData.sections || ["contact", "summary", "experience", "skills"],
				styles: templateData.styles,
				color_scheme: templateData.color_scheme || "classic",
				custom_colors: templateData.custom_colors || null,
				layout: templateData.layout || "standard",
				organization_uuid: userOrg.organization_uuid,
				created_by: user.id,
			})
			.select()
			.single();

		if (error)
			throw new Error(`Failed to create CV template: ${error.message}`);

		return mapDbRow(data);
	}

	static async update(id: string, templateData: UpdateCvTemplateData): Promise<CvTemplate> {
		const updates: any = {};
		if (templateData.name !== undefined) updates.name = templateData.name;
		if (templateData.description !== undefined) updates.description = templateData.description;
		if (templateData.content !== undefined) updates.content = templateData.content;
		if (templateData.header_html !== undefined) updates.header_html = templateData.header_html;
		if (templateData.footer_html !== undefined) updates.footer_html = templateData.footer_html;
		if (templateData.sections !== undefined) updates.sections = templateData.sections;
		if (templateData.styles !== undefined) updates.styles = templateData.styles;
		if (templateData.color_scheme !== undefined) updates.color_scheme = templateData.color_scheme;
		if (templateData.custom_colors !== undefined) updates.custom_colors = templateData.custom_colors;
		if (templateData.layout !== undefined) updates.layout = templateData.layout;
		if (templateData.is_default !== undefined) updates.is_default = templateData.is_default;
		updates.updated_at = new Date().toISOString();

		const { data, error } = await supabase
			.from('cv_templates')
			.update(updates)
			.eq('id', id)
			.select()
			.single();

		if (error)
			throw new Error(`Failed to update CV template: ${error.message}`);

		return mapDbRow(data);
	}

	static async delete(id: string): Promise<void> {
		const { error } = await supabase
			.from('cv_templates')
			.delete()
			.eq('id', id);

		if (error)
			throw new Error(`Failed to delete CV template: ${error.message}`);
	}

	static async duplicate(id: string, newName?: string): Promise<CvTemplate> {
		const template = await this.getById(id);
		if (!template)
			throw new Error('Template not found');

		const duplicatedTemplate: CreateCvTemplateData = {
			name: newName || `${template.name} (Copy)`,
			description: template.description,
			content: template.content,
			header_html: template.header_html,
			footer_html: template.footer_html,
			sections: template.sections,
			styles: template.styles,
		};

		return this.create(duplicatedTemplate);
	}
}
